import React, { useEffect } from "react";
import PropTypes from "prop-types";
import "./App.css";
import RouteObj from "./route";
import { Route, Switch, BrowserRouter } from "react-router-dom";
import { connect, Provider } from "react-redux";
import { ApplicationState } from "./redux/initialState";
import { AnyAction, Store } from "redux";
import Header from "./components/header/header";
import Footer from "./components/footer/footer";
import FullPageLoader from "./components/fullPageLoader/fullpageLoader";
import { PartnerLogo } from "./features/partnerLogo/partnerLogoTypes";
import { fetchPartnerLogo } from "./features/partnerLogo/partnerLogoActions";
import { ThunkDispatch } from "redux-thunk";
import { GelProvider } from "@tal-gel/components-v2";
import styled from "@emotion/styled";
import { useThemeSwitcher } from "./providers/ThemeProvider";
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import DynatraceScript from "./script/Dynatrace";

interface MainProps {
  store: Store<ApplicationState>;
  loading: boolean;
  data: PartnerLogo;
  errors?: string;
}

interface propsFromDispatch {
  fetchPartnerLogo: (PartnerLogo: any) => any;
}

type AllProps = MainProps & propsFromDispatch;

const App: React.FC<AllProps> = ({ store, loading, data, fetchPartnerLogo }) => {
  const path = window.location.pathname.substr(1).toLowerCase();
  useEffect(() => {
    if (path) {
      fetchPartnerLogo(path);
    }
  }, [fetchPartnerLogo, path]);

  DynatraceScript();

  const theme = useThemeSwitcher();
  const isAmp = theme.id === "amp";

  return (
    <Provider store={store}>
      <BrowserRouter>
        {(loading || !path) && (
          <GelProvider theme={theme}>
            <EmotionThemeProvider theme={theme}>
              <PageLayout>
                <PageHeader isAmp={isAmp}>
                  <Header {...data} />
                </PageHeader>
                <PageContent>
                  <Switch>
                    {RouteObj.map((route, index) => {
                      return <Route key={index} exact={route.isExact} path={route.path} component={route.component} />;
                    })}
                  </Switch>
                </PageContent>
                <PageFooter>
                  <div className={"Footer"}>
                    <Footer {...data} />
                  </div>
                </PageFooter>
              </PageLayout>
            </EmotionThemeProvider>
          </GelProvider>
        )}
        {!loading && path && <FullPageLoader />}
      </BrowserRouter>
    </Provider>
  );
};

const mapStateToProps = ({ partnerLogo }: ApplicationState) => ({
  loading: partnerLogo.loading,
  errors: partnerLogo.errors,
  data: partnerLogo.data,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  return {
    fetchPartnerLogo: (partnerLogo: PartnerLogo) => {
      dispatch(fetchPartnerLogo(partnerLogo));
    },
  };
};

App.propTypes = {
  store: PropTypes.any.isRequired,
  loading: PropTypes.any,
  data: PropTypes.any.isRequired,
  errors: PropTypes.string,
  fetchPartnerLogo: PropTypes.func.isRequired,
};

const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #fff;
  overflow-x: hidden;
`;
const PageHeader = styled.header<{ isAmp: boolean }>`
  width: 100%;
  border-bottom: ${({ isAmp }) => (isAmp ? "none" : "1px solid #e1e1e1")};
`;
const PageContent = styled.main`
  flex: 1;
  width: 100%;
  background: #fff;
`;
const PageFooter = styled.footer`
  width: 100%;
`;

export default connect(mapStateToProps, mapDispatchToProps)(App);
